import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  IconButton,
  Paper,
  TablePagination,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  Select,
  MenuItem,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TextField,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import Swal from "sweetalert2";

import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchWithdrawalRequests,
  reviewWithdrawalRequest,
} from "../services/memberWithdrawalService";

import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
const brand = "#800b33";
const brandHover = "#6c0a2b";

const STATUS_LABEL_KEYS = {
  PENDING_CHAIR_APPROVAL: "Waiting for Mwenyekiti",
  PENDING_SECRETARY_APPROVAL: "Waiting for Katibu",
  PENDING_ACCOUNTANT_APPROVAL: "Waiting for Muhasibu",
  APPROVED: "Finalized",
  REJECTED: "Rejected",
};

const statusColors = {
  PENDING_CHAIR_APPROVAL: "default",
  PENDING_SECRETARY_APPROVAL: "info",
  PENDING_ACCOUNTANT_APPROVAL: "warning",
  APPROVED: "success",
  REJECTED: "error",
};

const WORKFLOW_STEPS = ["Mwenyekiti", "Katibu", "Muhasibu", "Settlement"];

const stageStatusByRole = {
  Mwenyekiti: "PENDING_CHAIR_APPROVAL",
  Katibu: "PENDING_SECRETARY_APPROVAL",
  Muhasibu: "PENDING_ACCOUNTANT_APPROVAL",
};

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
  }),
};

const StatCard = ({ title, value, icon, index }) => (
  <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={index}>
    <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 52, height: 52, borderRadius: "50%",
            display: "grid", placeItems: "center",
            background: `${brand}1A`, color: brand,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary">{title}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const LeaderWithdrawalApprovals = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.role;
  const isLeadership = ["Mwenyekiti", "Katibu", "Muhasibu"].includes(role);

  const [initialLoading, setInitialLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const stageStatus = stageStatusByRole[role] || null;
  const statusLabel = useCallback((status) => t(STATUS_LABEL_KEYS[status] || status), [t]);
  const workflowStepLabels = useMemo(() => WORKFLOW_STEPS.map((label) => t(label)), [t]);

  const loadRequests = useCallback(
    async (silent = false) => {
      if (!isLeadership) {
        setInitialLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setInitialLoading(true);
      }
      setError(null);

      try {
        const data = await fetchWithdrawalRequests();
        setRequests(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || t("Unable to load withdrawal requests."));
      } finally {
        if (silent) {
          setRefreshing(false);
        } else {
          setInitialLoading(false);
        }
      }
    },
    [isLeadership]
  );

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailOpen(true);
  };

  const closeDialog = () => {
    setDetailOpen(false);
    setSelectedRequest(null);
  };

  const canActOnRequest = (request) => {
    if (!isLeadership || !stageStatus) return false;
    if (request.status === "REJECTED" || request.status === "APPROVED") return false;
    return request.status === stageStatus;
  };

  const handleApprove = async (request) => {
    const confirm = await Swal.fire({
      icon: "question",
      title: t("Approve withdrawal request?"),
      text: t("Approve withdrawal text", { member: request.member_name }),
      showCancelButton: true,
      confirmButtonColor: brand,
      cancelButtonColor: "#9e9e9e",
      confirmButtonText: t("Yes, proceed"),
    });
    if (!confirm.isConfirmed) return;

    setActioningId(request.id);
    try {
      await reviewWithdrawalRequest(request.id);
      await loadRequests(true);
      Swal.fire({
        icon: "success",
        title: t("Action recorded"),
        text: t("Workflow moved to next approver"),
        confirmButtonColor: brand,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("Unable to approve"),
        text: err.message || t("Please try again."),
        confirmButtonColor: brand,
      });
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (request) => {
    const result = await Swal.fire({
      icon: "warning",
      title: t("Reject withdrawal?"),
      text: t("Reject withdrawal note"),
      input: "textarea",
      inputLabel: t("Rejection reason"),
      inputPlaceholder: t("Rejection reason placeholder"),
      inputValidator: (value) => (!value ? t("Rejection reason is required.") : undefined),
      showCancelButton: true,
      confirmButtonText: t("Reject"),
      confirmButtonColor: "#c62828",
    });

    if (!result.isConfirmed) return;

    setActioningId(request.id);
    try {
      await reviewWithdrawalRequest(request.id, { rejection_reason: result.value });
      await loadRequests(true);
      Swal.fire({
        icon: "info",
        title: t("Request rejected"),
        text: t("Member notified", { member: request.member_name }),
        confirmButtonColor: brand,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("Unable to reject"),
        text: err.message || t("Please try again."),
        confirmButtonColor: brand,
      });
    } finally {
      setActioningId(null);
    }
  };

  const statusSummary = useMemo(() => {
    return requests.reduce(
      (acc, req) => {
        acc[req.status] = (acc[req.status] || 0) + 1;
        return acc;
      },
      {
        PENDING_CHAIR_APPROVAL: 0,
        PENDING_SECRETARY_APPROVAL: 0,
        PENDING_ACCOUNTANT_APPROVAL: 0,
        APPROVED: 0,
        REJECTED: 0,
      }
    );
  }, [requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter((request) => {
      const matchesStatus = statusFilter === "ALL" || request.status === statusFilter;
      const matchesSearch =
        !searchQuery ||
        request.member_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.reason.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [requests, searchQuery, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const currency = (amount) => {
    const numeric = Number(amount || 0);
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
    }).format(numeric);
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (initialLoading) {
    return <LoadingSpinner />;
  }

  if (!isLeadership) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="info">
          {t("Withdrawal approvals are only accessible to the Mwenyekiti, Katibu and Muhasibu roles.")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            {t("Member Withdrawal Approvals")}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t("Workflow: Mwenyekiti → Katibu → Muhasibu. Financial clearance is verified before the final payout.")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => loadRequests(true)}
          disabled={refreshing}
          sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
        >
          {refreshing ? t("Refreshing...") : t("Refresh")}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { labelKey: "Waiting for Mwenyekiti", status: "PENDING_CHAIR_APPROVAL" },
          { labelKey: "Waiting for Katibu", status: "PENDING_SECRETARY_APPROVAL" },
          { labelKey: "Waiting for Muhasibu", status: "PENDING_ACCOUNTANT_APPROVAL" },
          { labelKey: "Finalized", status: "APPROVED" },
        ].map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={item.status}>
            <StatCard
              title={t(item.labelKey)}
              value={statusSummary[item.status] || 0}
              icon={<VisibilityIcon />}
              index={index}
            />
          </Grid>
        ))}
      </Grid>

      <Alert severity="info" sx={{ mb: 3 }}>
        {t("Double-entry ledger entries and retention/refund calculations are created automatically after Muhasibu approval. Once finalized, the member becomes inactive and only reactivates when the group reinstates them.")}
      </Alert>

      <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder={t("Search by member name or reason...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("Status")}</InputLabel>
                <Select
                  label={t("Status")}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="ALL">{t("All Statuses")}</MenuItem>
                  {Object.entries(STATUS_LABEL_KEYS).map(([key, labelKey]) => (
                    <MenuItem key={key} value={key}>{t(labelKey)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={2}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Member")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Reason")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Submitted")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Approvals")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {t("Actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                          {t("No withdrawal requests match your filters.")}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((request, index) => {
                    const approvals = [
                      { key: "chair", labelKey: "Mwenyekiti", data: request.approvals?.chair },
                      { key: "secretary", labelKey: "Katibu", data: request.approvals?.secretary },
                      { key: "accountant", labelKey: "Muhasibu", data: request.approvals?.accountant },
                    ];
                    const disabled = !canActOnRequest(request);
                    return (
                      <TableRow key={request.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600 }}>{request.member_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.group_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Tooltip title={request.reason}>
                            <Typography noWrap>{request.reason}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabel(request.status)}
                            color={statusColors[request.status] || "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(request.created_at)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {approvals.map((step) => (
                              <Chip
                                key={step.key}
                                label={
                                  step.data?.approved
                                    ? t(step.labelKey)
                                    : t("Approval pending label", { role: t(step.labelKey) })
                                }
                                color={step.data?.approved ? "success" : "default"}
                                size="small"
                              />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t("View full details")}>
                            <IconButton onClick={() => handleViewDetails(request)} size="small">
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              disabled
                                ? t("Approve disabled tooltip")
                                : t("Approve & move to the next stage")
                            }
                          >
                            <span>
                              <IconButton
                                color="success"
                                onClick={() => handleApprove(request)}
                                disabled={disabled || actioningId === request.id}
                                size="small"
                              >
                                <CheckCircleOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              disabled
                                ? t("Reject disabled tooltip")
                                : t("Reject request")
                            }
                          >
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => handleReject(request)}
                                disabled={disabled || actioningId === request.id}
                                size="small"
                              >
                                <HighlightOffIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  }))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredRequests.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
      </motion.div>

      <Dialog open={detailOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
            {t("Withdrawal Request Details")}
          </Typography>
          <IconButton onClick={closeDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Stack spacing={3}>
              {selectedRequest.rejection_reason && (
                <Alert severity="error">
                  {t("Rejected: {{reason}}", { reason: selectedRequest.rejection_reason })}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("Member")}
                  </Typography>
                  <Typography variant="h6">{selectedRequest.member_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRequest.group_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("Submitted on")}
                  </Typography>
                  <Typography variant="h6">{formatDate(selectedRequest.created_at)}</Typography>
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t("Reason for withdrawal")}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2">{selectedRequest.reason}</Typography>
                </Paper>
              </Box>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">
                      {t("Retention Percent")}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: brand }}>
                      {selectedRequest.retention_percent}%
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">
                      {t("Retention Amount")}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: brand }}>
                      {currency(selectedRequest.retention_amount)}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">
                      {t("Refund Amount")}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: brand }}>
                      {currency(selectedRequest.refund_amount)}
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("Approval progress")}
                </Typography>
                <Stepper
                  activeStep={
                    {
                      PENDING_CHAIR_APPROVAL: 0,
                      PENDING_SECRETARY_APPROVAL: 1,
                      PENDING_ACCOUNTANT_APPROVAL: 2,
                      APPROVED: 3,
                    }[selectedRequest.status] ?? 0
                  }
                  alternativeLabel
                  sx={{
                    "& .MuiStepIcon-root.Mui-active": { color: brand },
                    "& .MuiStepIcon-root.Mui-completed": { color: brand },
                  }}
                >
                  {workflowStepLabels.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("Approval trail")}
                </Typography>
                <Grid container spacing={2}>
                  {["chair", "secretary", "accountant"].map((key) => {
                    const entry = selectedRequest.approvals?.[key];
                    const label = {
                      chair: t("Mwenyekiti"),
                      secretary: t("Katibu"),
                      accountant: t("Muhasibu"),
                    }[key];
                    return (
                      <Grid item xs={12} md={4} key={key}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
                          {entry?.approved ? (
                            <Typography variant="caption" color="text.secondary">
                              {t("Approved by {{name}} on {{date}}", {
                                name: entry.approved_by,
                                date: formatDate(entry.approved_at),
                              })}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {t("Awaiting action")}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaderWithdrawalApprovals;
